/**
 * Kitchen Sound Notification Service
 * Handles audio alerts for kitchen workflow events
 */

export interface SoundSettings {
  enabled: boolean;
  volume: number; // 0.0 to 1.0
  newOrderEnabled: boolean;
  orderReadyEnabled: boolean;
  takeawayReadyEnabled: boolean;
  fireKotEnabled: boolean;
  paymentSuccessEnabled: boolean;
}

export interface SoundEvent {
  type: 'new_order' | 'online_order' | 'order_ready' | 'takeaway_ready' | 'fire_kot' | 'payment_success';
  orderId: string;
  metadata?: Record<string, any>;
}

class KitchenSoundService {
  private audioContext: AudioContext | null = null;
  private settings: SoundSettings = {
    enabled: true,
    volume: 0.7,
    newOrderEnabled: true,
    orderReadyEnabled: true,
    takeawayReadyEnabled: true,
    fireKotEnabled: true,
    paymentSuccessEnabled: true,
  };
  
  private soundCache = new Map<string, AudioBuffer>();
  private isInitialized = false;
  private autoUnlockArmed = false;


  constructor() {
    this.loadSettings();
  }

  /**
   * Initialize audio context and load sound files
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Initialize audio context without microphone permissions
      await this.initializeAudioContext();

      // Load sound files
      await this.loadSoundFiles();

      this.isInitialized = true;
      console.log('✅ Kitchen Sound Service initialized');
    } catch (error) {
      console.error('❌ Failed to initialize Kitchen Sound Service:', error);
      throw error;
    }
  }

  /**
   * Initialize audio context without requiring microphone permissions
   */
  private async initializeAudioContext(): Promise<void> {
    if (!this.audioContext) {
      // Create AudioContext without microphone access
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

      // Resume context if suspended (required by some browsers)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
    }
  }

  /**
   * Ensure audio is allowed to play. Browsers keep the AudioContext `suspended`
   * until the user interacts with the page, so a passively-monitored tab would
   * otherwise drop the first alert silently. Call this on mount: it makes sure
   * the context exists, arms a one-time gesture listener to resume it, and
   * attempts an immediate resume (which only succeeds if a gesture already
   * happened).
   */
  async ensureUnlocked(): Promise<void> {
    if (!this.isInitialized) {
      try {
        await this.initialize();
      } catch {
        /* initialize already logs; nothing playable, bail */
        return;
      }
    }
    this.armAutoUnlock();
    if (this.audioContext && this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
      } catch {
        /* still needs a user gesture — armAutoUnlock will catch it */
      }
    }
  }

  /**
   * Attach a one-time listener that resumes audio on the first user gesture of
   * any kind, then detaches itself. Idempotent.
   */
  private armAutoUnlock(): void {
    if (this.autoUnlockArmed || typeof document === 'undefined') return;
    this.autoUnlockArmed = true;
    const handler = () => {
      this.audioContext?.resume().catch(() => {});
      document.removeEventListener('pointerdown', handler);
      document.removeEventListener('keydown', handler);
      document.removeEventListener('touchstart', handler);
    };
    const opts: AddEventListenerOptions = { passive: true };
    document.addEventListener('pointerdown', handler, opts);
    document.addEventListener('keydown', handler, opts);
    document.addEventListener('touchstart', handler, opts);
  }

  /**
   * Current AudioContext state, so the UI can show whether sound is armed.
   * 'running' = audible; 'suspended' = needs a user gesture; 'unavailable' =
   * no Web Audio support / not initialized.
   */
  getAudioState(): AudioContextState | 'unavailable' {
    return this.audioContext?.state ?? 'unavailable';
  }

  /**
   * Load sound files into memory
   */
  private async loadSoundFiles(): Promise<void> {
    const sounds = {
      new_order: '/sounds/kitchen/new-order.mp3',
      online_order: '/sounds/online-order-ring.mp3',
      order_ready: '/sounds/kitchen/order-ready.mp3',
      takeaway_ready: '/sounds/kitchen/takeaway-ready.mp3',
      fire_kot: '/sounds/kitchen/fire-kot.mp3',
      payment_success: '/sounds/kitchen/payment-success.mp3',
    };

    const loadPromises = Object.entries(sounds).map(async ([key, url]) => {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          // Fallback to generated sounds if files don't exist
          const buffer = await this.generateSound(key as keyof typeof sounds);
          this.soundCache.set(key, buffer);
          return;
        }
        
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await this.audioContext!.decodeAudioData(arrayBuffer);
        this.soundCache.set(key, audioBuffer);
      } catch (error) {
        console.warn(`Failed to load sound ${key}, using generated sound`);
        const buffer = await this.generateSound(key as keyof typeof sounds);
        this.soundCache.set(key, buffer);
      }
    });

    await Promise.all(loadPromises);
    console.log('✅ Sound files loaded');
  }

  /**
   * Generate programmatic sounds as fallback
   */
  private async generateSound(type: string): Promise<AudioBuffer> {
    if (!this.audioContext) throw new Error('Audio context not initialized');

    const sampleRate = this.audioContext.sampleRate;

    // New-order alert: deliberately loud, long and repeating so it carries
    // across a busy counter and an unattended order can't slip by. A "ding-dong"
    // two-note pattern repeated twice; only one tone sounds at a time, so the
    // peak stays within [-1, 1] (no clipping) even at high amplitude.
    if (type === 'new_order') {
      const duration = 1.2; // seconds — long enough to notice
      const buffer = this.audioContext.createBuffer(1, Math.floor(sampleRate * duration), sampleRate);
      const data = buffer.getChannelData(0);
      const notes = [880, 1175, 880, 1175]; // A5 / D6 alternating, repeated twice
      const noteDur = duration / notes.length;
      const peak = 0.9;
      for (let i = 0; i < data.length; i++) {
        const t = i / sampleRate;
        const noteIndex = Math.min(notes.length - 1, Math.floor(t / noteDur));
        const tInNote = t - noteIndex * noteDur;
        // Fast attack, exponential decay — leaves a brief gap before the next note.
        const env = Math.min(1, tInNote * 80) * Math.exp(-tInNote * 6);
        data[i] = Math.sin(tInNote * notes[noteIndex] * 2 * Math.PI) * env * peak;
      }
      return buffer;
    }

    // Online-order ring: a deliberately telephone-like "brrring" so the counter
    // instantly recognises a customer order (distinct from the kitchen ding-dong
    // above). Two close frequencies warble ~20Hz to give the classic ringing
    // timbre, delivered as two ~0.4s bursts with a gap — loud and unmistakable.
    if (type === 'online_order') {
      const duration = 1.6; // seconds
      const buffer = this.audioContext.createBuffer(1, Math.floor(sampleRate * duration), sampleRate);
      const data = buffer.getChannelData(0);
      const peak = 0.9;
      // Two ring bursts within the buffer, each burst on for ~0.45s then off.
      const bursts = [
        { start: 0.0, end: 0.45 },
        { start: 0.7, end: 1.15 },
      ];
      for (let i = 0; i < data.length; i++) {
        const t = i / sampleRate;
        const burst = bursts.find((b) => t >= b.start && t < b.end);
        if (!burst) {
          data[i] = 0;
          continue;
        }
        const tInBurst = t - burst.start;
        // Warble: carrier ~900Hz modulated by a 20Hz tremolo → ringing timbre.
        const carrier = Math.sin(t * 900 * 2 * Math.PI);
        const warble = 0.5 + 0.5 * Math.sin(t * 20 * 2 * Math.PI);
        // Gentle attack/release on each burst to avoid clicks.
        const env = Math.min(1, tInBurst * 60) * Math.min(1, (burst.end - t) * 60);
        data[i] = carrier * warble * env * peak;
      }
      return buffer;
    }

    const duration = 0.3; // 300ms
    const buffer = this.audioContext.createBuffer(1, sampleRate * duration, sampleRate);
    const channelData = buffer.getChannelData(0);

    // Generate different tones for different events
    // (new_order is handled above with its own louder, longer pattern)
    const frequencies = {
      order_ready: [600, 800, 1000], // Success sound
      takeaway_ready: [400, 600, 400], // Distinctive pattern
      fire_kot: [660, 990], // E5 + B5 — punchy "fired"
      payment_success: [523, 659, 784], // C major chord — warm "complete"
    };

    const freqs = frequencies[type as keyof typeof frequencies] || [440];
    
    for (let i = 0; i < channelData.length; i++) {
      const time = i / sampleRate;
      let sample = 0;
      
      freqs.forEach((freq) => {
        const phase = time * freq * 2 * Math.PI;
        const envelope = Math.exp(-time * 3); // Decay envelope
        sample += Math.sin(phase) * envelope * (0.3 / freqs.length);
      });
      
      channelData[i] = sample;
    }

    return buffer;
  }

  /**
   * Play sound for specific event
   */
  async playSound(event: SoundEvent): Promise<void> {
    if (!this.settings.enabled || !this.isInitialized || !this.audioContext) {
      return;
    }

    // Check if specific sound type is enabled
    const soundTypeEnabled = {
      new_order: this.settings.newOrderEnabled,
      // Online orders ride the same "new order" enable flag — no separate setting.
      online_order: this.settings.newOrderEnabled,
      order_ready: this.settings.orderReadyEnabled,
      takeaway_ready: this.settings.takeawayReadyEnabled,
      fire_kot: this.settings.fireKotEnabled,
      payment_success: this.settings.paymentSuccessEnabled,
    };

    if (!soundTypeEnabled[event.type]) {
      return;
    }

    try {
      // Ensure audio context is running (resume if suspended)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      const audioBuffer = this.soundCache.get(event.type);
      if (!audioBuffer) {
        console.warn(`Sound not found for event: ${event.type}`);
        return;
      }

      const source = this.audioContext.createBufferSource();
      const gainNode = this.audioContext.createGain();

      source.buffer = audioBuffer;
      gainNode.gain.value = this.settings.volume;

      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      source.start();

      console.log(`🔊 Playing sound for: ${event.type}`);
    } catch (error) {
      console.error('Failed to play sound:', error);
      // Try to resume audio context if it was suspended
      if (this.audioContext?.state === 'suspended') {
        try {
          await this.audioContext.resume();
        } catch (resumeError) {
          console.error('Failed to resume audio context:', resumeError);
        }
      }
    }
  }

  /**
   * Play new order notification
   */
  async playNewOrderSound(orderId: string): Promise<void> {
    await this.playSound({
      type: 'new_order',
      orderId,
      metadata: { timestamp: Date.now() }
    });
  }

  /**
   * Play the distinct online-order ring (telephone-style). Used by the counter
   * Online Orders inbox so a customer order is instantly recognisable and,
   * via the caller's nag loop, repeats until the queue is attended.
   */
  async playOnlineOrderRing(orderId: string): Promise<void> {
    await this.playSound({
      type: 'online_order',
      orderId,
      metadata: { timestamp: Date.now() }
    });
  }

  /**
   * Play order ready notification
   */
  async playOrderReadySound(orderId: string, orderType: string): Promise<void> {
    const soundType = orderType === 'takeout' ? 'takeaway_ready' : 'order_ready';
    await this.playSound({
      type: soundType,
      orderId,
      metadata: { orderType, timestamp: Date.now() }
    });
  }

  /**
   * Play fire-KOT confirmation
   */
  async playFireKotSound(orderId: string): Promise<void> {
    await this.playSound({
      type: 'fire_kot',
      orderId,
      metadata: { timestamp: Date.now() }
    });
  }

  /**
   * Play payment-success confirmation
   */
  async playPaymentSuccessSound(orderId: string): Promise<void> {
    await this.playSound({
      type: 'payment_success',
      orderId,
      metadata: { timestamp: Date.now() }
    });
  }

  /**
   * Short, quiet UI "tick" for tactile feedback when a cook checks/un-checks a
   * line on the KDS. Deliberately tiny and distinct from the alert chimes — a
   * soft confirmation, not a notification — and gated by the same master toggle,
   * so the on-screen mute silences it too. Synthesized on the fly (no buffer /
   * asset) and best-effort: it must never throw into the tap handler.
   *
   * @param variant 'on' = a brighter up-tick for checking an item prepared;
   *                'off' = a softer down-tick for undoing.
   */
  playTick(variant: 'on' | 'off' = 'on'): void {
    if (!this.settings.enabled || !this.audioContext) return;
    try {
      const ctx = this.audioContext;
      if (ctx.state === 'suspended') void ctx.resume();
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = variant === 'on' ? 1320 : 660;
      // Quiet relative to the alerts; still scales with the master volume.
      const peak = Math.max(0.0001, 0.18 * this.settings.volume);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(peak, now + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.07);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.08);
    } catch {
      /* feedback sound is best-effort; never disrupt the tap */
    }
  }

  /**
   * Update sound settings
   */
  updateSettings(newSettings: Partial<SoundSettings>): void {
    this.settings = { ...this.settings, ...newSettings };
    this.saveSettings();
    console.log('🔧 Sound settings updated:', this.settings);
  }

  /**
   * Get current settings
   */
  getSettings(): SoundSettings {
    return { ...this.settings };
  }

  /**
   * Load settings from localStorage
   */
  private loadSettings(): void {
    try {
      const stored = localStorage.getItem('kitchen_sound_settings');
      if (stored) {
        this.settings = { ...this.settings, ...JSON.parse(stored) };
      }
    } catch (error) {
      console.warn('Failed to load sound settings:', error);
    }
  }

  /**
   * Save settings to localStorage
   */
  private saveSettings(): void {
    try {
      localStorage.setItem('kitchen_sound_settings', JSON.stringify(this.settings));
    } catch (error) {
      console.warn('Failed to save sound settings:', error);
    }
  }

  /**
   * Test sound playback
   */
  async testSound(type: SoundEvent['type']): Promise<void> {
    await this.playSound({
      type,
      orderId: 'test-order',
      metadata: { test: true }
    });
  }

  /**
   * Cleanup resources
   */
  dispose(): void {
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.soundCache.clear();
    this.isInitialized = false;
    console.log('🧹 Kitchen Sound Service disposed');
  }
}

// Create singleton instance
export const kitchenSoundService = new KitchenSoundService();

// Auto-initialize on first import (with error handling)
kitchenSoundService.initialize().catch(error => {
  console.warn('Kitchen Sound Service initialization failed:', error);
});

export default kitchenSoundService;
